'use client';
import { Button } from '@components/Button';
import ImageWrapper from '@components/Image';
import DateRange from '@components/Search/DateRange';
import InfiniteHits from '@components/Search/InfiniteHits';
import { NoResults } from '@components/Search/NoResults';
import NumberOfHits from '@components/Search/NumberOfHits';
import PriceRange from '@components/Search/PriceRange';
import RefinementList from '@components/Search/RefinementList';
import ResetFilters from '@components/Search/ResetFilters';
import SearchInput from '@components/Search/SearchInput';
import Typography from '@components/Typography';
import algoliasearch from 'algoliasearch/lite';

import clsx from 'clsx';
import { useState } from 'react';
import { Configure, useInstantSearch } from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';
import { userStore } from 'store/UserStore';
import { AttributesToRetrieve, HideFilters } from 'types/Algolia';

const atdwIndex = process.env.NEXT_PUBLIC_ALGOLIA_INDEX;

type SearchPageProps = {
  url?: string;
};

function NoResultsBoundary({ children, fallback }) {
  const { results } = useInstantSearch();

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

export default function SearchPage({ url }: SearchPageProps) {
  const [filtersOpen, toggleFilters] = useState(false);
  const { uuid } = userStore();

  return (
    <div className="py-16 md:px-4 md:py-24">
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex min-h-[600px] flex-col md:flex-row md:space-x-12">
          <InstantSearchNext
            searchClient={algoliasearch(
              'GN3FTQV485',
              process.env.NEXT_PUBLIC_ALGOLIA_VSC_KEY
            )}
            indexName={atdwIndex}
            insights={true}
            future={{
              preserveSharedStateOnUnmount: true,
            }}
            routing={{
              router: {
                cleanUrlOnDispose: false,
              },
            }}>
            <Configure
              facetFilters="owningOrganisationNumber:-OR0052562"
              filters={HideFilters}
              attributesToRetrieve={AttributesToRetrieve}
              //numericFilters={[`endDateTimeStamp < 0`]}
              hitsPerPage={18}
              analytics={true}
              clickAnalytics
              enablePersonalization
              userToken={uuid ? uuid.toString() : 'anonymous-user'}
            />
            <div className="relative shrink-0 md:w-60">
              {/* Filter buttons */}

              <div
                className={clsx(
                  'space-y-8',
                  { hidden: !filtersOpen },
                  'relative z-0 w-full bg-white px-6 py-5 md:block md:w-auto md:border-none md:bg-transparent md:p-0'
                )}>
                <Typography tag="h2" size="text-xl" className="border-b py-2.5">
                  Filters
                </Typography>
                <RefinementList
                  label="I'm Travelling With"
                  attribute="travellerTypes"
                  limit={4}
                  showMore
                  sort="alphabetical"
                />
                <RefinementList
                  label="I'm Looking For"
                  attribute="productCategoryId"
                  limit={5}
                  showMore
                  sort="alphabetical"
                />
                <DateRange
                  attribute="endDateTimestamp"
                  enabled="event"
                  precision={-3}
                />
                <PriceRange
                  attribute="rates.priceFrom"
                  enabled="accomm"
                  items={[
                    { label: '$', start: 1, end: 150 },
                    { label: '$$', start: 150, end: 300 },
                    { label: '$$$', start: 300 },
                  ]}
                />
                <RefinementList
                  label="Accommodation Type"
                  enabled="accomm"
                  attribute="verticalClassifications.productTypeId"
                  showMore
                  limit={6}
                />
                <RefinementList
                  label="TripAdvisor Rating"
                  enabled="accomm"
                  attribute="tripAdvisorDetails.rating"
                  showMore
                  sort="alphabetical-reverse"
                  prettifier="stars"
                  limit={6}
                />
                <RefinementList
                  label="Deal Type"
                  enabled="deal"
                  attribute="parentProductCategoryId"
                  showMore
                  limit={6}
                />
                <RefinementList
                  label="Event Type"
                  enabled="event"
                  attribute="verticalClassifications.productTypeId"
                  showMore
                  limit={6}
                />
                <RefinementList
                  label="I'm Interested In"
                  attribute="contentPillar"
                  limit={4}
                  showMore
                  sort="alphabetical"
                />

                <RefinementList
                  label="Area"
                  attribute="addresses.cityName"
                  showMore
                  limit={6}
                />

                <div className="full-width sticky inset-x-0 bottom-0 border-t border-t-gray-400 bg-white px-6 py-2 md:hidden">
                  <Button
                    size="default"
                    className="w-full"
                    action={() => toggleFilters(!filtersOpen)}>
                    Show results
                  </Button>
                </div>
              </div>
            </div>
            <div
              className={clsx('z-0 px-4 md:flex-1 md:px-0', {
                hidden: filtersOpen,
              })}>
              <div className="flex w-full items-center space-x-2 pb-4 pt-6">
                <SearchInput />
                <div onClick={() => toggleFilters(!filtersOpen)}>
                  <div
                    className={clsx(
                      'flex h-full items-center space-x-2 rounded-lg border border-gray-400 px-3 py-3 font-semibold md:hidden',
                      {
                        'bg-brand-mothernature text-white': filtersOpen,
                      }
                    )}>
                    <ImageWrapper
                      src={'/icons/search/filter.svg'}
                      alt="filter-icon"
                      className="relative size-4"
                      dimensions={{ width: 20, height: 20 }}
                    />{' '}
                    <span>Filters</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between md:pt-4">
                <NumberOfHits />

                <ResetFilters />
              </div>
              <NoResultsBoundary fallback={<NoResults />}>
                <InfiniteHits />
              </NoResultsBoundary>
            </div>
          </InstantSearchNext>
        </div>
      </div>
    </div>
  );
}
